import clsx from 'clsx';
import React, { useContext, useEffect, useId, useRef, useState } from 'react';
import type { OnboardingPayload } from '../../../types/onboardingIntake';
import {
  Autocomplete,
  Button,
  Callout,
  Checkbox,
  Chip,
  type DropFile,
  Field,
  FileDrop,
  Input,
  Radio,
  Row,
  IconLock,
  Select,
  Tag,
  Textarea,
} from '../../../ui/v2';
import {
  ACCEPTED_FILE_COPY,
  ACCEPTED_FILE_TYPES,
} from '../../../utils/fileUploadValidation';
import { phoneExtensionInput } from '../../../utils/phoneExtension';
import {
  type AddressSuggestion,
  resolveAddress,
  searchAddresses,
} from './address';
import {
  applyLegalAddress,
  applyRestaurantAddress,
  clearLegalAddress,
  clearRestaurantAddress,
  clearDisabledTransfers,
  closingCutoffMinutes,
  type FieldErrors,
  hasService,
  humanTransfersDisabled,
  type IntakeDraft,
  ORDER_CUTOFFS,
  type PickedAddress,
  representativeFromContact,
  SERVICES,
  type StepKey,
  summarize,
} from './model';

/**
 * The eight step forms of the onboarding intake. Each step is a plain
 * function of the draft: it reads `draft`, writes through `update`, and
 * shows `errors` (keyed by dotted path) next to the control they belong to.
 * The page owns navigation, validation timing and submission.
 *
 * Layout: a step is one or more `Section`s (13px title, one-line
 * description, hairline between) with fields in a 1–2 column `Grid`. No
 * cards. Every conditional field appears directly under the choice that
 * revealed it.
 */
export interface StepProps {
  draft: IntakeDraft;
  update: (fn: (d: IntakeDraft) => IntakeDraft) => void;
  errors: FieldErrors;
  clearError: (key: string) => void;
  /** Jump to another step, e.g. back to an earlier answer that blocks a choice. */
  goToStep?: (step: StepKey) => void;
}

type Payload = OnboardingPayload;
const setPayload = (update: StepProps['update'], fn: (p: Payload) => Payload) =>
  update((d) => ({ ...d, payload: fn(d.payload) }));
const patch = <S extends keyof Payload>(
  update: StepProps['update'],
  section: S,
  values: Partial<Payload[S]>,
) =>
  setPayload(update, (p) => ({
    ...p,
    [section]: { ...(p[section] as object), ...values },
  }));

// ---------------------------------------------------------------------------
// Where an answer came from
// ---------------------------------------------------------------------------

/**
 * For an intake that starts from another location's answers: the tag to show
 * beside a field (keyed like errors, by dotted path), or null. The plain
 * intake leaves it unset, so no field is tagged.
 */
export const ProvenanceContext = React.createContext<
  (key: string) => React.ReactNode
>(() => null);

const Mark: React.FC<{ k: string }> = ({ k }) => (
  <>{useContext(ProvenanceContext)(k)}</>
);

/** A checkbox with its answer's tag beside it. */
const MarkedCheckbox: React.FC<
  React.ComponentProps<typeof Checkbox> & { k: string }
> = ({ k, ...rest }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
    <Checkbox {...rest} />
    <Mark k={k} />
  </div>
);

// ---------------------------------------------------------------------------
// Layout and field helpers
// ---------------------------------------------------------------------------

const Section: React.FC<{
  title: string;
  description?: React.ReactNode;
  first?: boolean;
  /** Tag the whole section's answer (a checklist with no field label). */
  mark?: string;
  children: React.ReactNode;
}> = ({ title, description, first, mark, children }) => (
  <section
    aria-label={title}
    className={clsx(!first && 'mt-7 border-t border-v2-line pt-6')}
  >
    <h3 className="flex items-center gap-1.5 text-v2-13 font-semibold text-v2-fg">
      {title}
      {mark && <Mark k={mark} />}
    </h3>
    {description && (
      <p className="mt-0.5 max-w-[68ch] text-v2-12 leading-[18px] text-v2-fg-3">
        {description}
      </p>
    )}
    <div className="mt-4 flex flex-col gap-4">{children}</div>
  </section>
);

const Grid: React.FC<{ cols?: 1 | 2; children: React.ReactNode }> = ({
  cols = 2,
  children,
}) => (
  <div
    className={clsx(
      'grid grid-cols-1 gap-x-4 gap-y-4',
      cols === 2 && 'sm:grid-cols-2',
    )}
  >
    {children}
  </div>
);

/** Wraps a control so the page can find and focus the first invalid one. */
const Slot: React.FC<{
  k: string;
  children: React.ReactNode;
  className?: string;
}> = ({ k, children, className }) => (
  <div data-field={k} className={className}>
    {children}
  </div>
);

interface ErrorProps {
  errors: FieldErrors;
  clearError: (k: string) => void;
}

const TextField: React.FC<
  ErrorProps & {
    k: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    type?: string;
    hint?: string;
    optional?: boolean;
    placeholder?: string;
    autoComplete?: string;
    inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
    span?: boolean;
  }
> = ({
  k,
  label,
  value,
  onChange,
  errors,
  clearError,
  type = 'text',
  hint,
  optional,
  placeholder,
  autoComplete,
  inputMode,
  span,
}) => (
  <Slot k={k} className={span ? 'sm:col-span-2' : undefined}>
    <Field
      label={label}
      hint={hint}
      error={errors[k]}
      optional={optional}
      adornment={<Mark k={k} />}
    >
      {(id, describedBy) => (
        <Input
          id={id}
          type={type}
          inputMode={inputMode}
          autoComplete={autoComplete}
          placeholder={placeholder}
          aria-describedby={describedBy}
          invalid={!!errors[k]}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (errors[k]) clearError(k);
          }}
        />
      )}
    </Field>
  </Slot>
);

const AreaField: React.FC<
  ErrorProps & {
    k: string;
    label: string;
    value: string;
    onChange: (v: string) => void;
    hint?: string;
    optional?: boolean;
    placeholder?: string;
    rows?: number;
    span?: boolean;
  }
> = ({
  k,
  label,
  value,
  onChange,
  errors,
  clearError,
  hint,
  optional,
  placeholder,
  rows = 3,
  span,
}) => (
  <Slot k={k} className={span ? 'sm:col-span-2' : undefined}>
    <Field
      label={label}
      hint={hint}
      error={errors[k]}
      optional={optional}
      adornment={<Mark k={k} />}
    >
      {(id, describedBy) => (
        <Textarea
          id={id}
          rows={rows}
          placeholder={placeholder}
          aria-describedby={describedBy}
          invalid={!!errors[k]}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (errors[k]) clearError(k);
          }}
          className={rows <= 2 ? 'min-h-[44px]' : undefined}
        />
      )}
    </Field>
  </Slot>
);

const NumberField: React.FC<{
  k: string;
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  hint?: string;
  optional?: boolean;
  errors?: FieldErrors;
  clearError?: (k: string) => void;
}> = ({ k, label, value, onChange, hint, optional, errors, clearError }) => (
  <Slot k={k}>
    <Field
      label={label}
      hint={hint}
      error={errors?.[k]}
      optional={optional}
      adornment={<Mark k={k} />}
    >
      {(id, describedBy) => (
        <Input
          id={id}
          type="number"
          inputMode="numeric"
          min={0}
          aria-describedby={describedBy}
          invalid={!!errors?.[k]}
          value={value ?? ''}
          onChange={(e) => {
            onChange(
              e.target.value === '' ? undefined : Number(e.target.value),
            );
            if (errors?.[k]) clearError?.(k);
          }}
          className="v2-num"
        />
      )}
    </Field>
  </Slot>
);

const InlineError: React.FC<{ children?: string }> = ({ children }) =>
  children ? (
    <p role="alert" className="mt-1.5 text-v2-12 text-v2-danger">
      {children}
    </p>
  ) : null;

function RadioGroup<T extends string>({
  k,
  label,
  description,
  value,
  options,
  onChange,
  errors,
  clearError,
}: ErrorProps & {
  k: string;
  label: string;
  description?: string;
  value: T | undefined;
  options: {
    value: T;
    label: string;
    description?: string;
    disabled?: boolean;
    disabledReason?: React.ReactNode;
  }[];
  onChange: (v: T) => void;
}) {
  const name = useId();
  const err = errors[k];
  return (
    <Slot k={k}>
      <fieldset
        role="radiogroup"
        aria-label={label}
        aria-invalid={!!err || undefined}
        className="min-w-0"
      >
        <legend className="flex items-center gap-1.5 text-v2-12 font-medium text-v2-fg-2">
          {label}
          <Mark k={k} />
        </legend>
        {description && (
          <p className="mt-0.5 text-v2-12 text-v2-fg-3">{description}</p>
        )}
        <div className="mt-2 flex flex-col gap-2">
          {options.map((o) => (
            <div key={o.value}>
              <Radio
                name={name}
                label={o.label}
                description={o.description}
                disabled={o.disabled}
                checked={value === o.value}
                onChange={() => {
                  if (o.disabled) return;
                  onChange(o.value);
                  if (err) clearError(k);
                }}
              />
              {o.disabled && o.disabledReason ? (
                <p className="ml-6 mt-1 text-v2-12 text-v2-fg-3">
                  {o.disabledReason}
                </p>
              ) : null}
            </div>
          ))}
        </div>
        <InlineError>{err}</InlineError>
      </fieldset>
    </Slot>
  );
}

/**
 * A transfer choice the earlier "no transfers" answer rules out. The note is
 * neutral (it's a consequence of their own answer, not an error) and links
 * back to the question so they can change it.
 */
const blockedTransfer = (
  disabled: boolean,
  goToStep?: StepProps['goToStep'],
) =>
  disabled
    ? {
        disabled: true,
        disabledReason: (
          <>
            Off because you chose to have Arthur message staff instead of
            transferring.
            {goToStep && (
              <>
                {' '}
                <button
                  type="button"
                  className="underline hover:text-v2-fg"
                  onClick={() => goToStep('services')}
                >
                  Change this
                </button>
              </>
            )}
          </>
        ),
      }
    : {};

const toggle = (arr: string[], v: string) =>
  arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

/**
 * Address lookup. Picking a suggestion shows its text at once, then fetches
 * the place details (street, city, postal code; time zone for the
 * restaurant). The pick does not write the draft: Continue stays disabled
 * and the saved draft is held until the fetch finishes, so a refresh can't
 * store a suggestion with no street, city or postal code. Typing cancels
 * the fetch and clears the structured parts. Leaving the step does the
 * same, so a result that comes back later can't overwrite a newer pick or
 * clear the wait.
 */
const AddressField: React.FC<
  ErrorProps & {
    k: string;
    label: string;
    hint?: string;
    value: string;
    selected: boolean;
    withTimezone?: boolean;
    onText: (v: string) => void;
    onPick: (a: PickedAddress) => void;
    onResolvingChange?: (resolving: boolean) => void;
  }
> = ({
  k,
  label,
  hint,
  value,
  selected,
  withTimezone,
  onText,
  onPick,
  onResolvingChange,
  errors,
  clearError,
}) => {
  const seq = useRef(0);
  const [resolving, setResolving] = useState(false);
  const [pendingLabel, setPendingLabel] = useState<string | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const onResolvingRef = useRef(onResolvingChange);
  onResolvingRef.current = onResolvingChange;
  const error = errors[k] ?? lookupError ?? undefined;

  const setBusy = (busy: boolean) => {
    setResolving(busy);
    onResolvingRef.current?.(busy);
  };

  useEffect(
    () => () => {
      seq.current += 1;
      onResolvingRef.current?.(false);
    },
    [],
  );

  const pick = async (s: AddressSuggestion) => {
    const id = ++seq.current;
    setPendingLabel(s.label);
    clearError(k);
    setLookupError(null);
    setBusy(true);
    try {
      const picked = await resolveAddress(s, { withTimezone });
      if (seq.current !== id) return;
      onPick(picked);
      setPendingLabel(null);
    } catch {
      if (seq.current !== id) return;
      // The lookup failed, so the text is only a typed address now.
      onText(s.label);
      setPendingLabel(null);
      setLookupError('Couldn’t read that address. Pick it again.');
    } finally {
      if (seq.current === id) setBusy(false);
    }
  };

  return (
    <Slot k={k} className="sm:col-span-2">
      <Field
        label={label}
        hint={
          error
            ? undefined
            : resolving
              ? 'Checking the address…'
              : selected
                ? 'Street, city and postal code captured.'
                : (hint ?? 'Start typing and pick the address from the list.')
        }
        error={error}
        adornment={<Mark k={k} />}
      >
        {(id, describedBy) => (
          <Autocomplete<AddressSuggestion>
            id={id}
            aria-describedby={describedBy}
            invalid={!!error}
            value={pendingLabel ?? value}
            onChange={(t) => {
              seq.current += 1;
              setPendingLabel(null);
              setBusy(false);
              setLookupError(null);
              onText(t);
              if (errors[k]) clearError(k);
            }}
            onSelect={(s) => void pick(s)}
            load={searchAddresses}
            keyOf={(s) => s.id}
            renderOption={(s) => ({
              primary: s.primary,
              secondary: s.secondary,
            })}
            minLength={3}
            emptyText="No matching address. Keep typing, or add the street number."
            errorText="Address lookup isn’t available right now. Try again in a moment."
            placeholder="184 Grand St, Jersey City"
          />
        )}
      </Field>
    </Slot>
  );
};

// ---------------------------------------------------------------------------
// 1 · Your restaurant
// ---------------------------------------------------------------------------

export const RestaurantStep: React.FC<
  StepProps & {
    prefilled?: boolean;
    onAddressResolving?: (resolving: boolean) => void;
    /** A location added by its group: setup is fixed to that group. */
    group?: string;
  }
> = ({
  draft,
  update,
  errors,
  clearError,
  prefilled,
  onAddressResolving,
  group,
}) => {
  const p = draft.payload;
  const f = { errors, clearError };
  const addressBusy = useRef({ legal: false, restaurant: false });
  const onAddressResolvingRef = useRef(onAddressResolving);
  onAddressResolvingRef.current = onAddressResolving;
  const reportAddress = (which: 'legal' | 'restaurant', busy: boolean) => {
    addressBusy.current[which] = busy;
    onAddressResolvingRef.current?.(
      addressBusy.current.legal || addressBusy.current.restaurant,
    );
  };
  useEffect(() => () => onAddressResolvingRef.current?.(false), []);
  return (
    <>
      {prefilled && (
        <Callout
          tone="neutral"
          className="mb-6"
          title="Filled in from your activation"
        >
          We copied the business name and your details from checkout. Check them
          and continue.
        </Callout>
      )}
      <Section
        first
        title="Names"
        description="What guests call you, and what's on the paperwork."
      >
        <Grid>
          <TextField
            k="business.restaurant_name"
            label="Restaurant name"
            value={p.business.restaurant_name}
            onChange={(v) => patch(update, 'business', { restaurant_name: v })}
            autoComplete="organization"
            {...f}
          />
          <TextField
            k="business.legal_business_name"
            label="Legal business name"
            placeholder="e.g. Grand Street Hospitality LLC"
            value={p.business.legal_business_name}
            onChange={(v) =>
              patch(update, 'business', { legal_business_name: v })
            }
            {...f}
          />
          <TextField
            k="registration.tax_id"
            label="Tax ID"
            hint="EIN, or a Canadian Business Number."
            value={p.registration.tax_id}
            onChange={(v) =>
              patch(update, 'registration', {
                tax_id: v,
                canadian_business_number: v,
              })
            }
            inputMode="numeric"
            {...f}
          />
          <TextField
            k="business.website"
            label="Website"
            type="url"
            inputMode="url"
            placeholder="restaurant.com"
            value={p.business.website}
            onChange={(v) => patch(update, 'business', { website: v })}
            autoComplete="url"
            {...f}
          />
        </Grid>
      </Section>
      <Section
        title="Addresses"
        description="Both come from a lookup so we get street, city and postal code exactly as the tax office and the map have them."
      >
        <Grid>
          <AddressField
            k="location.legal"
            label="Legal business address"
            value={p.location.legal_business_address_raw}
            selected={draft.legalAddressSelected}
            onText={(t) =>
              update((d) => ({
                ...d,
                legalAddressSelected: false,
                payload: clearLegalAddress(d.payload, t),
              }))
            }
            onPick={(a) =>
              update((d) => ({
                ...d,
                legalAddressSelected: true,
                payload: applyLegalAddress(d.payload, a),
              }))
            }
            onResolvingChange={(busy) => reportAddress('legal', busy)}
            {...f}
          />
          <AddressField
            k="location.restaurant"
            label="Restaurant address"
            hint="Where guests come. Also sets the store's time zone."
            value={p.location.restaurant_address_raw}
            selected={draft.restaurantAddressSelected}
            withTimezone
            onText={(t) =>
              update((d) => ({
                ...d,
                restaurantAddressSelected: false,
                payload: clearRestaurantAddress(d.payload, t),
              }))
            }
            onPick={(a) =>
              update((d) => ({
                ...d,
                restaurantAddressSelected: true,
                payload: applyRestaurantAddress(d.payload, a),
              }))
            }
            onResolvingChange={(busy) => reportAddress('restaurant', busy)}
            {...f}
          />
          {draft.legalAddressSelected &&
            draft.restaurantAddressSelected &&
            p.location.legal_business_address_raw !==
              p.location.restaurant_address_raw && (
              <p className="-mt-2 text-v2-12 text-v2-fg-3 sm:col-span-2">
                Two different addresses — that's fine; both are kept.
              </p>
            )}
          <TextField
            k="location.phone"
            label="Restaurant phone"
            type="tel"
            inputMode="tel"
            hint="The number guests call today. Arthur answers it."
            value={p.location.phone}
            onChange={(v) => patch(update, 'location', { phone: v })}
            autoComplete="tel"
            {...f}
          />
        </Grid>
      </Section>
      {group && (
        <Section title="Restaurant group">
          <div className="flex items-center justify-between gap-3 rounded-v2 bg-v2-sunken px-3 py-2.5 text-v2-13">
            <span className="flex items-center gap-2 text-v2-fg-2">
              <IconLock size={14} className="shrink-0 text-v2-fg-3" />
              <span>
                Part of <b className="font-semibold text-v2-fg">{group}</b>
              </span>
            </span>
            <span className="text-v2-12 text-v2-fg-3">Set by your group</span>
          </div>
        </Section>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// 2 · Who we work with
// ---------------------------------------------------------------------------

/** Someone the location may want as its primary contact (group onboarding). */
export interface SuggestedContact {
  name: string;
  email: string;
  phone: string;
  role: string;
}

export const PeopleStep: React.FC<
  StepProps & { suggestions?: SuggestedContact[] }
> = ({ draft, update, errors, clearError, suggestions }) => {
  const p = draft.payload;
  const f = { errors, clearError };
  const setContact = (values: Partial<Payload['primary_contact']>) =>
    update((d) => {
      const primary_contact = { ...d.payload.primary_contact, ...values };
      return {
        ...d,
        payload: {
          ...d.payload,
          primary_contact,
          authorized_representative: d.sameRepresentative
            ? representativeFromContact(primary_contact)
            : d.payload.authorized_representative,
        },
      };
    });
  return (
    <>
      <Section
        first
        title="Primary contact"
        description="Who we talk to during setup and whenever something needs a person."
      >
        {suggestions && suggestions.length > 0 && (
          <div
            role="group"
            aria-label="Suggested contacts"
            className="flex flex-wrap items-center gap-2"
          >
            <span className="text-v2-12 font-medium text-v2-fg-3">
              Suggested
            </span>
            {suggestions.map((c) => (
              <Chip
                key={c.email}
                pressed={
                  p.primary_contact.email.trim().toLowerCase() ===
                  c.email.toLowerCase()
                }
                title={`${c.email} · ${c.role}`}
                onClick={() => {
                  setContact({
                    name: c.name,
                    email: c.email,
                    phone: c.phone || p.primary_contact.phone,
                  });
                  [
                    'primary_contact.name',
                    'primary_contact.email',
                    'primary_contact.phone',
                  ].forEach(clearError);
                }}
              >
                {c.name}
              </Chip>
            ))}
          </div>
        )}
        <Grid>
          <TextField
            k="primary_contact.name"
            label="Name"
            value={p.primary_contact.name}
            onChange={(v) => setContact({ name: v })}
            autoComplete="name"
            {...f}
          />
          <TextField
            k="primary_contact.title_role"
            label="Title or role"
            placeholder="Owner, GM, Operations…"
            value={p.primary_contact.title_role}
            onChange={(v) => setContact({ title_role: v })}
            autoComplete="organization-title"
            {...f}
          />
          <TextField
            k="primary_contact.phone"
            label="Best phone"
            type="tel"
            inputMode="tel"
            value={p.primary_contact.phone}
            onChange={(v) => setContact({ phone: v })}
            autoComplete="tel"
            {...f}
          />
          <TextField
            k="primary_contact.email"
            label="Email"
            type="email"
            inputMode="email"
            value={p.primary_contact.email}
            onChange={(v) => setContact({ email: v })}
            autoComplete="email"
            {...f}
          />
        </Grid>
      </Section>
      <Section
        title="Authorised representative"
        description="The person who can sign for the business. Often the same person."
      >
        <Checkbox
          label="Same as the primary contact"
          checked={draft.sameRepresentative}
          onChange={(e) => {
            const same = e.target.checked;
            update((d) => ({
              ...d,
              sameRepresentative: same,
              payload: same
                ? {
                    ...d.payload,
                    authorized_representative: representativeFromContact(
                      d.payload.primary_contact,
                    ),
                  }
                : d.payload,
            }));
            ['name', 'email', 'phone'].forEach((x) =>
              clearError(`authorized_representative.${x}`),
            );
          }}
        />
        {!draft.sameRepresentative && (
          <Grid>
            <TextField
              k="authorized_representative.name"
              label="Name"
              value={p.authorized_representative.name}
              onChange={(v) =>
                patch(update, 'authorized_representative', { name: v })
              }
              {...f}
            />
            <TextField
              k="authorized_representative.business_title"
              label="Business title"
              optional
              value={p.authorized_representative.business_title}
              onChange={(v) =>
                patch(update, 'authorized_representative', {
                  business_title: v,
                })
              }
              {...f}
            />
            <TextField
              k="authorized_representative.email"
              label="Email"
              type="email"
              inputMode="email"
              value={p.authorized_representative.email}
              onChange={(v) =>
                patch(update, 'authorized_representative', { email: v })
              }
              {...f}
            />
            <TextField
              k="authorized_representative.phone"
              label="Phone"
              type="tel"
              inputMode="tel"
              optional
              value={p.authorized_representative.phone}
              onChange={(v) =>
                patch(update, 'authorized_representative', { phone: v })
              }
              {...f}
            />
            <TextField
              k="authorized_representative.job_position"
              label="Job position"
              optional
              hint="If it differs from the title on the paperwork."
              value={p.authorized_representative.job_position}
              onChange={(v) =>
                patch(update, 'authorized_representative', { job_position: v })
              }
              {...f}
            />
          </Grid>
        )}
      </Section>
    </>
  );
};

// ---------------------------------------------------------------------------
// 3 · What Arthur handles
// ---------------------------------------------------------------------------

const SERVICE_DESCRIPTION: Record<(typeof SERVICES)[number], string> = {
  'Phone orders': 'Take, link or transfer phone orders.',
  Reservations: 'Book tables, handle changes and late guests.',
  Events: 'Collect private-event inquiries for your team.',
  Catering: 'Route catering requests to the right person.',
};

export const ServicesStep: React.FC<StepProps> = ({
  draft,
  update,
  errors,
  clearError,
}) => {
  const p = draft.payload;
  const f = { errors, clearError };
  const err = errors['business.services_used'];
  return (
    <>
      <Section
        first
        title="Services"
        description="Pick what Arthur should take on. The next steps only ask about what you pick — you can add more later from Store info."
        mark="business.services_used"
      >
        <Slot k="business.services_used">
          <fieldset aria-label="Services" aria-invalid={!!err || undefined}>
            <ul className="divide-y divide-v2-line border-y border-v2-line">
              {SERVICES.map((s) => (
                <li key={s} className="py-2.5">
                  <Checkbox
                    label={s}
                    checked={hasService(p, s)}
                    onChange={() => {
                      patch(update, 'business', {
                        services_used: toggle(p.business.services_used, s),
                      });
                      if (err) clearError('business.services_used');
                    }}
                    className="items-start font-medium [&>input]:mt-0.5"
                  />
                  <p className="ml-6 text-v2-12 text-v2-fg-3">
                    {SERVICE_DESCRIPTION[s]}
                  </p>
                </li>
              ))}
            </ul>
            <InlineError>{err}</InlineError>
          </fieldset>
        </Slot>
      </Section>
      <Section
        title="Transfers"
        description="This applies to every later choice that would hand the caller to your team."
      >
        <RadioGroup
          k="call_handling.human_transfer_enabled"
          label="Do you want Arthur to transfer calls to a human if the caller asks?"
          value={
            p.call_handling.human_transfer_enabled == null
              ? undefined
              : p.call_handling.human_transfer_enabled
                ? 'yes'
                : 'no'
          }
          options={[
            { value: 'yes', label: 'Yes' },
            {
              value: 'no',
              label: 'No, send a text/email to staff instead',
            },
          ]}
          onChange={(v) => {
            update((d) => ({
              ...d,
              payload: clearDisabledTransfers({
                ...d.payload,
                call_handling: {
                  ...d.payload.call_handling,
                  human_transfer_enabled: v === 'yes',
                },
              }),
            }));
            if (errors['call_handling.human_transfer_enabled'])
              clearError('call_handling.human_transfer_enabled');
          }}
          {...f}
        />
        {p.call_handling.human_transfer_enabled && (
          <Grid>
            <TextField
              k="call_handling.transfer_phone_number"
              label="Transfer calls to"
              type="tel"
              inputMode="tel"
              optional
              hint="Usually the host stand or a manager. If you skip it, we’ll ask during setup."
              value={p.call_handling.transfer_phone_number ?? ''}
              onChange={(v) =>
                patch(update, 'call_handling', { transfer_phone_number: v })
              }
              {...f}
            />
            <TextField
              k="call_handling.transfer_phone_number_ext"
              label="Extension"
              optional
              placeholder="123#"
              hint="Digits and # only. Leave blank if there isn’t one."
              value={p.call_handling.transfer_phone_number_ext ?? ''}
              onChange={(v) =>
                patch(update, 'call_handling', {
                  transfer_phone_number_ext: phoneExtensionInput(v),
                })
              }
              {...f}
            />
          </Grid>
        )}
      </Section>
      <Section title="Anything else">
        <AreaField
          k="business.biggest_pain_point"
          label="What's the biggest pain on the phone today?"
          optional
          rows={3}
          placeholder="e.g. We miss 30 calls a night during service."
          value={p.business.biggest_pain_point}
          onChange={(v) => patch(update, 'business', { biggest_pain_point: v })}
          {...f}
        />
      </Section>
    </>
  );
};

// ---------------------------------------------------------------------------
// 4 · Phone orders
// ---------------------------------------------------------------------------

export const OrdersStep: React.FC<StepProps> = ({
  draft,
  update,
  errors,
  clearError,
  goToStep,
}) => {
  const o = draft.payload.ordering;
  const f = { errors, clearError };
  const transferBlocked = blockedTransfer(
    humanTransfersDisabled(draft.payload),
    goToStep,
  );
  const set = (values: Partial<Payload['ordering']>) =>
    patch(update, 'ordering', values);
  return (
    <>
      <Section first title="Phone orders">
        <RadioGroup
          k="ordering.phone_order_handling"
          label="When someone calls to order"
          value={o.phone_order_handling}
          options={[
            {
              value: 'agent_takes_order',
              label: 'Arthur takes the order',
              description: 'Best when Arthur is connected to your menu or POS.',
            },
            {
              value: 'send_order_link',
              label: 'Arthur texts an ordering link',
              description: 'The caller finishes on their phone.',
            },
            {
              value: 'transfer_to_store',
              label: 'Transfer the call to the store',
              ...transferBlocked,
            },
            {
              value: 'do_not_accept',
              label: 'Say we don’t take phone orders',
            },
          ]}
          onChange={(v) => set({ phone_order_handling: v })}
          {...f}
        />
        {o.phone_order_handling === 'send_order_link' && (
          <Grid>
            <TextField
              k="ordering.ordering_link"
              label="Ordering link"
              type="url"
              inputMode="url"
              value={o.ordering_link ?? ''}
              onChange={(v) => set({ ordering_link: v, ordering_url: v })}
              {...f}
            />
          </Grid>
        )}
        {o.phone_order_handling === 'transfer_to_store' && (
          <Grid>
            <TextField
              k="ordering.order_requests_phone_number"
              label="Transfer orders to"
              type="tel"
              inputMode="tel"
              optional
              hint="Leave blank to use your main transfer number."
              value={o.order_requests_phone_number ?? ''}
              onChange={(v) => set({ order_requests_phone_number: v })}
              {...f}
            />
            <TextField
              k="ordering.order_requests_phone_number_ext"
              label="Extension"
              optional
              placeholder="123#"
              hint="Digits and # only. Leave blank if there isn’t one."
              value={o.order_requests_phone_number_ext ?? ''}
              onChange={(v) =>
                set({
                  order_requests_phone_number_ext: phoneExtensionInput(v),
                })
              }
              {...f}
            />
          </Grid>
        )}
        {o.phone_order_handling &&
          o.phone_order_handling !== 'do_not_accept' && (
            <Grid>
              <Field
                label="Stop taking orders before close"
                hint="Arthur tells late callers the kitchen is closing."
              >
                {(id, describedBy) => (
                  <Select
                    id={id}
                    aria-describedby={describedBy}
                    value={
                      draft.customOrderCutoff
                        ? 'other'
                        : String(closingCutoffMinutes(o) ?? 0)
                    }
                    onChange={(e) => {
                      const other = e.target.value === 'other';
                      update((d) => ({
                        ...d,
                        customOrderCutoff: other,
                        payload: {
                          ...d.payload,
                          ordering: {
                            ...d.payload.ordering,
                            last_order_offset_minutes: other
                              ? undefined
                              : Number(e.target.value),
                          },
                        },
                      }));
                      clearError('ordering.last_order_offset_minutes');
                    }}
                  >
                    {ORDER_CUTOFFS.map((m) => (
                      <option key={m} value={m}>
                        {m === 0
                          ? 'Right at closing'
                          : m >= 60
                            ? `${m / 60} hour before`
                            : `${m} minutes before`}
                      </option>
                    ))}
                    <option value="other">Other</option>
                  </Select>
                )}
              </Field>
              {draft.customOrderCutoff && (
                <NumberField
                  k="ordering.last_order_offset_minutes"
                  label="Minutes before close"
                  value={o.last_order_offset_minutes}
                  onChange={(v) => set({ last_order_offset_minutes: v })}
                  {...f}
                />
              )}
            </Grid>
          )}
      </Section>
      <Section title="Delivery">
        <RadioGroup
          k="ordering.delivery_handling"
          label="When someone asks about delivery"
          value={o.delivery_handling}
          options={[
            {
              value: 'agent_takes_delivery',
              label: 'Arthur takes delivery orders',
            },
            {
              value: 'explain_third_party_delivery',
              label: 'Arthur explains how to order delivery',
              description: 'For DoorDash, Uber Eats and the like.',
            },
            { value: 'pickup_only', label: 'Pickup only' },
          ]}
          onChange={(v) => set({ delivery_handling: v })}
          {...f}
        />
        {o.delivery_handling === 'explain_third_party_delivery' && (
          <AreaField
            k="ordering.how_to_order_delivery_message"
            label="What should Arthur say?"
            rows={2}
            placeholder="Delivery is through DoorDash and Uber Eats — search for our name."
            value={o.how_to_order_delivery_message ?? ''}
            onChange={(v) => set({ how_to_order_delivery_message: v })}
            {...f}
          />
        )}
      </Section>
      <Section title="Payment">
        <RadioGroup
          k="ordering.payment_handling"
          label="How do phone orders get paid?"
          value={o.payment_handling}
          options={[
            {
              value: 'online_only',
              label: 'Pay online before the order goes to the kitchen',
              description: 'Prevents no-show orders.',
            },
            { value: 'pay_on_site', label: 'Pay at pickup' },
          ]}
          onChange={(v) => set({ payment_handling: v })}
          {...f}
        />
      </Section>
      <Section
        title="Menu and POS"
        description="Helps us connect the menu Arthur reads from."
      >
        <Grid>
          <TextField
            k="ordering.pos_other_name"
            label="POS or ordering system"
            optional
            placeholder="Toast, Square, Clover…"
            value={
              o.pos_other_name ??
              (typeof o.pos_system === 'string' ? o.pos_system : '')
            }
            onChange={(v) => set({ pos_other_name: v, pos_system: v })}
            {...f}
          />
          <TextField
            k="ordering.menu_link"
            label="Menu link"
            type="url"
            inputMode="url"
            optional
            value={
              o.menu_link ?? (typeof o.menu_url === 'string' ? o.menu_url : '')
            }
            onChange={(v) => set({ menu_link: v, menu_url: v })}
            {...f}
          />
          <NumberField
            k="ordering.average_lunch_prep_time_minutes"
            label="Lunch prep time (minutes)"
            optional
            value={o.average_lunch_prep_time_minutes}
            onChange={(v) =>
              set({
                average_lunch_prep_time_minutes: v,
                average_prep_time_lunch_minutes: v ?? null,
              })
            }
          />
          <NumberField
            k="ordering.average_dinner_prep_time_minutes"
            label="Dinner prep time (minutes)"
            optional
            value={o.average_dinner_prep_time_minutes}
            onChange={(v) =>
              set({
                average_dinner_prep_time_minutes: v,
                average_prep_time_dinner_minutes: v ?? null,
              })
            }
          />
        </Grid>
      </Section>
    </>
  );
};

// ---------------------------------------------------------------------------
// 5 · Reservations
// ---------------------------------------------------------------------------

export const ReservationsStep: React.FC<StepProps> = ({
  draft,
  update,
  errors,
  clearError,
  goToStep,
}) => {
  const r = draft.payload.reservations;
  const f = { errors, clearError };
  const transferBlocked = blockedTransfer(
    humanTransfersDisabled(draft.payload),
    goToStep,
  );
  const set = (values: Partial<Payload['reservations']>) =>
    patch(update, 'reservations', values);
  return (
    <>
      <Section first title="Bookings">
        <RadioGroup
          k="reservations.handling"
          label="When someone calls to book"
          value={r.handling}
          options={[
            {
              value: 'book_directly',
              label: 'Arthur books the table',
              description:
                'Needs a connected reservation system, e.g. OpenTable.',
            },
            { value: 'send_link', label: 'Arthur texts a booking link' },
          ]}
          onChange={(v) => set({ handling: v })}
          {...f}
        />
        {r.handling === 'send_link' && (
          <Grid>
            <TextField
              k="reservations.reservation_link"
              label="Booking link"
              type="url"
              inputMode="url"
              value={r.reservation_link ?? ''}
              onChange={(v) => set({ reservation_link: v })}
              {...f}
            />
          </Grid>
        )}
      </Section>
      <Section title="Details">
        <Grid>
          <TextField
            k="reservations.reservation_email"
            label="Reservations email"
            type="email"
            inputMode="email"
            optional
            hint="Where booking notes go."
            value={r.reservation_email ?? ''}
            onChange={(v) => set({ reservation_email: v })}
            {...f}
          />
          <NumberField
            k="reservations.max_party_size"
            label="Largest party Arthur may book"
            optional={r.handling !== 'book_directly'}
            hint={
              r.handling === 'book_directly'
                ? 'Bigger groups are handed to you.'
                : 'Only used when Arthur books through OpenTable.'
            }
            value={r.max_party_size}
            onChange={(v) => set({ max_party_size: v })}
            {...f}
          />
          <NumberField
            k="reservations.default_grace_period_minutes"
            label="Grace period for late guests (minutes)"
            optional
            value={r.default_grace_period_minutes}
            onChange={(v) => set({ default_grace_period_minutes: v })}
          />
          <div className="flex flex-col justify-end gap-2 pb-1">
            <MarkedCheckbox
              k="reservations.special_requests_allowed"
              label="Guests can add special requests"
              checked={Boolean(r.special_requests_allowed)}
              onChange={(e) =>
                set({ special_requests_allowed: e.target.checked })
              }
            />
            <MarkedCheckbox
              k="reservations.seating_preferences_supported"
              label="Guests can ask for a seating preference"
              checked={Boolean(r.seating_preferences_supported)}
              onChange={(e) =>
                set({ seating_preferences_supported: e.target.checked })
              }
            />
          </div>
        </Grid>
      </Section>
      <Section title="When things change">
        <Grid cols={1}>
          <RadioGroup
            k="reservations.late_handling"
            label="A guest says they’ll arrive after the grace period"
            value={r.late_handling}
            options={[
              { value: 'notify_staff', label: 'Text the staff' },
              {
                value: 'transfer',
                label: 'Transfer the call to staff',
                ...transferBlocked,
              },
            ]}
            onChange={(v) => set({ late_handling: v })}
            {...f}
          />
          <RadioGroup
            k="reservations.cancellation_failure_handling"
            label="OpenTable/Resy does not allow changes or cancelations on a booking"
            value={r.cancellation_failure_handling}
            options={[
              { value: 'notify_staff', label: 'Text the staff' },
              {
                value: 'transfer',
                label: 'Transfer the call to staff',
                ...transferBlocked,
              },
            ]}
            onChange={(v) => set({ cancellation_failure_handling: v })}
            {...f}
          />
        </Grid>
      </Section>
    </>
  );
};

// ---------------------------------------------------------------------------
// 6 · Events & catering
// ---------------------------------------------------------------------------

export const EventsStep: React.FC<StepProps> = ({
  draft,
  update,
  errors,
  clearError,
  goToStep,
}) => {
  const p = draft.payload;
  const f = { errors, clearError };
  const ev = p.events;
  const c = p.catering;
  const setEv = (values: Partial<Payload['events']>) =>
    patch(update, 'events', values);
  const setC = (values: Partial<Payload['catering']>) =>
    patch(update, 'catering', values);
  const transferBlocked = blockedTransfer(humanTransfersDisabled(p), goToStep);
  return (
    <>
      {hasService(p, 'Events') && (
        <>
          <Section first title="Private events">
            <RadioGroup
              k="events.handling"
              label="When someone asks about an event"
              value={ev.handling === 'decline' ? undefined : ev.handling}
              options={[
                {
                  value: 'collect_lead',
                  label: 'Arthur collects the details and your team follows up',
                  description:
                    'Inquiries land in Events on your dashboard, with a transcript.',
                },
                { value: 'text_link', label: 'Arthur texts an events link' },
                {
                  value: 'transfer',
                  label: 'Transfer the call to the events team',
                  ...transferBlocked,
                },
              ]}
              onChange={(v) => setEv({ handling: v })}
              {...f}
            />
            {ev.handling === 'text_link' && (
              <Grid>
                <TextField
                  k="events.event_link"
                  label="Events link"
                  type="url"
                  inputMode="url"
                  value={ev.event_link ?? ''}
                  onChange={(v) => setEv({ event_link: v })}
                  {...f}
                />
              </Grid>
            )}
            {ev.handling === 'transfer' && (
              <Grid>
                <TextField
                  k="events.event_forwarding_number"
                  label="Transfer events to"
                  type="tel"
                  inputMode="tel"
                  value={ev.event_forwarding_number ?? ''}
                  onChange={(v) => setEv({ event_forwarding_number: v })}
                  {...f}
                />
                <TextField
                  k="events.event_forwarding_number_ext"
                  label="Extension"
                  optional
                  placeholder="123#"
                  hint="Digits and # only. Leave blank if there isn’t one."
                  value={ev.event_forwarding_number_ext ?? ''}
                  onChange={(v) =>
                    setEv({
                      event_forwarding_number_ext: phoneExtensionInput(v),
                    })
                  }
                  {...f}
                />
              </Grid>
            )}
          </Section>
        </>
      )}
      {hasService(p, 'Catering') && (
        <>
          <Section
            first={!hasService(p, 'Events')}
            title="Catering"
            description="What Arthur does when someone asks about catering."
          >
            <RadioGroup
              k="catering.handling"
              label="When someone asks about catering"
              value={c.handling === 'decline' ? undefined : c.handling}
              options={[
                {
                  value: 'collect_lead',
                  label: 'Arthur collects the details and your team follows up',
                  description:
                    'Arthur asks a few questions and passes the request to your team. It doesn’t place the catering order.',
                },
                {
                  value: 'provide_contact',
                  label: 'Arthur texts a catering link',
                  description:
                    'Your catering menu or order form. The caller finishes on their phone.',
                },
                {
                  value: 'transfer',
                  label: 'Transfer the call to a catering contact',
                  ...transferBlocked,
                },
              ]}
              onChange={(v) => setC({ handling: v })}
              {...f}
            />
            {c.handling === 'transfer' && (
              <Grid>
                <TextField
                  k="catering.catering_forwarding_number"
                  label="Transfer catering to"
                  type="tel"
                  inputMode="tel"
                  value={c.catering_forwarding_number ?? ''}
                  onChange={(v) => setC({ catering_forwarding_number: v })}
                  {...f}
                />
                <TextField
                  k="catering.catering_forwarding_number_ext"
                  label="Extension"
                  optional
                  placeholder="123#"
                  hint="Digits and # only. Leave blank if there isn’t one."
                  value={c.catering_forwarding_number_ext ?? ''}
                  onChange={(v) =>
                    setC({
                      catering_forwarding_number_ext: phoneExtensionInput(v),
                    })
                  }
                  {...f}
                />
              </Grid>
            )}
            {c.handling === 'provide_contact' && (
              <Grid>
                <TextField
                  k="catering.catering_link"
                  label="Catering link"
                  type="url"
                  inputMode="url"
                  value={c.catering_link ?? ''}
                  onChange={(v) => setC({ catering_link: v })}
                  {...f}
                />
              </Grid>
            )}
          </Section>
        </>
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// 7 · Guest questions
// ---------------------------------------------------------------------------

export const FaqsStep: React.FC<
  StepProps & {
    files: DropFile[];
    onAddFiles: (files: File[]) => void;
    onRemoveFile: (id: string) => void;
    disabled?: boolean;
  }
> = ({
  draft,
  update,
  errors,
  clearError,
  files,
  onAddFiles,
  onRemoveFile,
  disabled,
}) => {
  const p = draft.payload;
  const f = { errors, clearError };
  return (
    <>
      <Section first title="Gift cards">
        <RadioGroup
          k="gift_cards.handling"
          label="When a caller asks about gift cards"
          value={
            p.gift_cards.handling === 'explain_only'
              ? undefined
              : p.gift_cards.handling
          }
          options={[
            { value: 'send_link', label: 'Text them a link to buy one' },
            { value: 'unavailable', label: 'We don’t sell gift cards' },
          ]}
          onChange={(v) => patch(update, 'gift_cards', { handling: v })}
          {...f}
        />
        {p.gift_cards.handling === 'send_link' && (
          <Grid>
            <TextField
              k="gift_cards.gift_card_link"
              label="Gift card link"
              type="url"
              inputMode="url"
              value={p.gift_cards.gift_card_link ?? ''}
              onChange={(v) =>
                patch(update, 'gift_cards', { gift_card_link: v })
              }
              {...f}
            />
          </Grid>
        )}
      </Section>
      <Section title="Parking">
        <Grid>
          <AreaField
            k="store_faqs.parking_info"
            label="What should Arthur say about parking?"
            optional
            rows={2}
            placeholder="Street parking; garage on Newark Ave."
            value={p.store_faqs.parking_info ?? ''}
            onChange={(v) => patch(update, 'store_faqs', { parking_info: v })}
            {...f}
          />
        </Grid>
      </Section>
      <Section title="FAQs">
        <Grid>
          <TextField
            k="knowledge_base.faq_url"
            label="FAQ page"
            type="url"
            inputMode="url"
            optional
            value={p.knowledge_base.faq_url}
            onChange={(v) => patch(update, 'knowledge_base', { faq_url: v })}
            {...f}
          />
        </Grid>
      </Section>
      <Section
        title="Documents"
        description="FAQ docs, menus, allergen info, and house policies like Wi-Fi, dress code or pets — anything Arthur should read. Optional; you can email them later too."
      >
        <FileDrop
          files={files}
          onAdd={onAddFiles}
          onRemove={onRemoveFile}
          accept={ACCEPTED_FILE_TYPES}
          hint={ACCEPTED_FILE_COPY}
          label="Upload documents"
          disabled={disabled}
        />
      </Section>
    </>
  );
};

// ---------------------------------------------------------------------------
// 8 · Review
// ---------------------------------------------------------------------------

export const ReviewStep: React.FC<
  StepProps & {
    /** Shown read-only so a refresh that lost them is visible before submit. */
    files: DropFile[];
    onEdit: (step: StepKey) => void;
    /** Who confirmed each step and when, for a shared intake link. */
    confirmations?: Partial<Record<StepKey, string>>;
  }
> = ({ draft, files, onEdit, confirmations }) => {
  const sections = summarize(draft).map((s) =>
    s.step === 'faqs'
      ? {
          ...s,
          rows: [
            ...s.rows,
            {
              label: 'Documents',
              value: files.length
                ? files.map((file) => file.name).join(', ')
                : null,
            },
          ],
        }
      : s,
  );
  return (
    <>
      <Section
        first
        title="Review"
        description="A last look. Edit takes you to the step; your answers are kept."
      >
        <div className="flex flex-col gap-5">
          {sections.map((s) => (
            <div key={s.step}>
              <div className="flex h-6 items-center justify-between">
                <h4 className="flex items-center gap-2 text-v2-12 font-semibold text-v2-fg-3">
                  {s.title}
                  {confirmations?.[s.step] && (
                    <Tag tone="success">{confirmations[s.step]}</Tag>
                  )}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(s.step)}
                  aria-label={`Edit ${s.title}`}
                >
                  Edit
                </Button>
              </div>
              <dl className="mt-1">
                {s.rows.map((r) => (
                  <Row key={r.label} label={r.label} empty={r.value == null}>
                    {r.value ?? 'Not given'}
                  </Row>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </Section>
    </>
  );
};
